import { FormInstance } from 'antd';
import type { UploadFile } from 'antd/es/upload/interface';
import dayjs from 'dayjs';
import useIndexedDbUploadDraft from 'hooks/useIndexedDbUploadDraft';
import useMinimizablePersistedDraft from 'hooks/useMinimizablePersistedDraft';
import useMinimizeAnimation from 'hooks/useMinimizeAnimation';
import { RefObject, useCallback, useEffect, useRef, useState } from 'react';
import { NouveauDossierConcessionnaireData } from 'types/Concessionnaire';

const STEPS = 3;
const DRAFT_STORAGE_NAMESPACE = 'hellio:forms:nouveau-dossier-concessionnaire';
const DRAFT_VERSION = 1;

interface SerializedDraftValues extends Omit<
  Partial<NouveauDossierConcessionnaireData>,
  | 'dateDevis'
  | 'dateEditionDevis'
  | 'datePrevisionnelleDevis'
  | 'dateLivraison'
  | 'devis'
  | 'otherFiles'
  | 'avisImposition'
> {
  dateDevis?: string;
  dateEditionDevis?: string;
  datePrevisionnelleDevis?: string;
  dateLivraison?: string;
}

interface ConcessionnaireDraft {
  currentStep: number;
  values?: SerializedDraftValues;
}

interface UseConcessionnaireDraftSessionArgs {
  open: boolean;
  initialStep: number;
  onClose: () => void;
  userId?: string | number;
  form: FormInstance<NouveauDossierConcessionnaireData>;
  isSubmitSuccess: boolean;
  resetStep2Mutation: () => void;
  resetStep3Mutation: () => void;
}

interface UseConcessionnaireDraftSessionReturn {
  isHydrated: boolean;
  currentStepIndex: number;
  setCurrentStepIndex: (stepIndex: number) => void;
  isWizardOpen: boolean;
  isSuccessOpen: boolean;
  isCloseConfirmationOpen: boolean;
  isDraftActive: boolean;
  isDraftMinimized: boolean;
  restoredAvisImpositionFiles?: UploadFile[];
  onDevisChange: (files?: NouveauDossierConcessionnaireData['devis']) => void;
  onOtherFilesChange: (
    files?: NouveauDossierConcessionnaireData['otherFiles'],
  ) => void;
  onAvisImpositionChange: (
    files?: NouveauDossierConcessionnaireData['avisImposition'],
  ) => void;
  requestMinimize: () => void;
  expandFromMinimized: () => void;
  isMinimizing: boolean;
  minimizeTargetRef: RefObject<HTMLDivElement | null>;
  requestClose: () => void;
  cancelClose: () => void;
  confirmClose: () => void;
  closeAfterSuccess: () => void;
}

function clampDraftStep(step: number) {
  return Math.max(1, Math.min(STEPS, Math.floor(step)));
}

function clampStepIndex(stepIndex: number) {
  return Math.max(0, Math.min(STEPS - 1, Math.floor(stepIndex)));
}

function createInitialDraft(initialStep: number): ConcessionnaireDraft {
  return {
    currentStep: clampDraftStep(initialStep),
    values: undefined,
  };
}

function serializeDraftValues(
  values: Partial<NouveauDossierConcessionnaireData>,
): SerializedDraftValues {
  const sanitizedValues: Partial<NouveauDossierConcessionnaireData> = {
    ...values,
    devis: undefined,
    otherFiles: undefined,
    avisImposition: undefined,
  };

  const {
    dateDevis,
    dateEditionDevis,
    datePrevisionnelleDevis,
    dateLivraison,
    ...rest
  } = sanitizedValues;

  return {
    ...rest,
    dateDevis: dateDevis ? dayjs(dateDevis).toISOString() : undefined,
    dateEditionDevis: dateEditionDevis
      ? dayjs(dateEditionDevis).toISOString()
      : undefined,
    datePrevisionnelleDevis: datePrevisionnelleDevis
      ? dayjs(datePrevisionnelleDevis).toISOString()
      : undefined,
    dateLivraison: dateLivraison
      ? dayjs(dateLivraison).toISOString()
      : undefined,
  };
}

function deserializeDraftValues(
  values?: SerializedDraftValues,
): Partial<NouveauDossierConcessionnaireData> {
  if (!values) {
    return {};
  }

  const {
    dateDevis,
    dateEditionDevis,
    datePrevisionnelleDevis,
    dateLivraison,
    ...rest
  } = values;

  return {
    ...rest,
    dateDevis: dateDevis ? dayjs(dateDevis) : undefined,
    dateEditionDevis: dateEditionDevis ? dayjs(dateEditionDevis) : undefined,
    datePrevisionnelleDevis: datePrevisionnelleDevis
      ? dayjs(datePrevisionnelleDevis)
      : undefined,
    dateLivraison: dateLivraison ? dayjs(dateLivraison) : undefined,
  };
}

export default function useConcessionnaireDraftSession({
  open,
  initialStep,
  onClose,
  userId,
  form,
  isSubmitSuccess,
  resetStep2Mutation,
  resetStep3Mutation,
}: UseConcessionnaireDraftSessionArgs): UseConcessionnaireDraftSessionReturn {
  const [currentStepIndex, setCurrentStepIndexState] = useState(() =>
    clampStepIndex(initialStep - 1),
  );
  const [isCloseConfirmationOpen, setIsCloseConfirmationOpen] = useState(false);
  const [restoredAvisImpositionFiles, setRestoredAvisImpositionFiles] =
    useState<UploadFile[]>();
  const hydratedDraftRef = useRef(false);
  const isRestoringDraftRef = useRef(false);
  const wasOpenRef = useRef(false);

  const {
    isHydrated,
    storageKey,
    state: draftState,
    startDraft,
    patchDraft,
    minimizeDraft,
    expandDraft,
    clearDraft,
    clearPersistedDraft,
  } = useMinimizablePersistedDraft<ConcessionnaireDraft>({
    storageNamespace: DRAFT_STORAGE_NAMESPACE,
    userScope: userId,
    draftVersion: DRAFT_VERSION,
    expandOnRestore: false,
    createInitialDraft: () => createInitialDraft(initialStep),
  });

  const {
    persistFiles: persistDevisFiles,
    restoreFiles: restoreDevisFiles,
    clearFiles: clearDevisFiles,
  } = useIndexedDbUploadDraft({
    storageKey,
    entry: `devis:v${DRAFT_VERSION}`,
  });
  const {
    persistFiles: persistOtherFiles,
    restoreFiles: restoreOtherFiles,
    clearFiles: clearOtherFiles,
  } = useIndexedDbUploadDraft({
    storageKey,
    entry: `other-files:v${DRAFT_VERSION}`,
  });
  const {
    persistFiles: persistAvisImpositionFiles,
    restoreFiles: restoreAvisImpositionFiles,
    clearFiles: clearAvisImpositionFiles,
  } = useIndexedDbUploadDraft({
    storageKey,
    entry: `avis-imposition:v${DRAFT_VERSION}`,
  });
  const resetFormFlow = useCallback(() => {
    setCurrentStepIndexState(0);
    setRestoredAvisImpositionFiles(undefined);
    form.resetFields();
    resetStep2Mutation();
    resetStep3Mutation();
  }, [form, resetStep2Mutation, resetStep3Mutation]);

  const clearPersistedFiles = useCallback(() => {
    Promise.all([
      clearDevisFiles(),
      clearOtherFiles(),
      clearAvisImpositionFiles(),
    ]).catch(() => undefined);
  }, [clearAvisImpositionFiles, clearDevisFiles, clearOtherFiles]);

  const persistCurrentSnapshot = useCallback(
    (nextStepIndex = currentStepIndex) => {
      if (
        !draftState.isActive ||
        !isHydrated ||
        !hydratedDraftRef.current ||
        isRestoringDraftRef.current
      ) {
        return;
      }

      const currentValues = form.getFieldsValue(
        true,
      ) as Partial<NouveauDossierConcessionnaireData>;
      const valuesWithFiles: Partial<NouveauDossierConcessionnaireData> = {
        ...currentValues,
        devis:
          (form.getFieldValue('devis') as UploadFile[] | undefined) ??
          currentValues.devis,
        otherFiles:
          (form.getFieldValue('otherFiles') as UploadFile[] | undefined) ??
          currentValues.otherFiles,
        avisImposition:
          (form.getFieldValue('avisImposition') as UploadFile[] | undefined) ??
          currentValues.avisImposition,
      };

      patchDraft({
        currentStep: clampStepIndex(nextStepIndex) + 1,
        values: serializeDraftValues(valuesWithFiles),
      });

      if (Array.isArray(valuesWithFiles.devis)) {
        persistDevisFiles(valuesWithFiles.devis).catch(() => undefined);
      }
      if (Array.isArray(valuesWithFiles.otherFiles)) {
        persistOtherFiles(valuesWithFiles.otherFiles).catch(() => undefined);
      }
      if (Array.isArray(valuesWithFiles.avisImposition)) {
        persistAvisImpositionFiles(valuesWithFiles.avisImposition).catch(
          () => undefined,
        );
      }
    },
    [
      currentStepIndex,
      draftState.isActive,
      form,
      isHydrated,
      patchDraft,
      persistAvisImpositionFiles,
      persistDevisFiles,
      persistOtherFiles,
    ],
  );

  const {
    isMinimizing,
    requestMinimize,
    targetRef: minimizeTargetRef,
  } = useMinimizeAnimation({
    isMinimized: draftState.isMinimized,
    sourceSelector: '.multi-step-modal .ant-modal-content',
    onBeforeMinimize: persistCurrentSnapshot,
    onMinimize: minimizeDraft,
  });

  const onDevisChange = useCallback(
    (files?: NouveauDossierConcessionnaireData['devis']) => {
      if (!draftState.isActive || !isHydrated) {
        return;
      }

      persistDevisFiles(files).catch(() => undefined);
    },
    [draftState.isActive, isHydrated, persistDevisFiles],
  );

  const onOtherFilesChange = useCallback(
    (files?: NouveauDossierConcessionnaireData['otherFiles']) => {
      if (!draftState.isActive || !isHydrated) {
        return;
      }

      persistOtherFiles(files).catch(() => undefined);
    },
    [draftState.isActive, isHydrated, persistOtherFiles],
  );

  const onAvisImpositionChange = useCallback(
    (files?: NouveauDossierConcessionnaireData['avisImposition']) => {
      if (!draftState.isActive || !isHydrated) {
        return;
      }

      persistAvisImpositionFiles(files).catch(() => undefined);
    },
    [draftState.isActive, isHydrated, persistAvisImpositionFiles],
  );

  const setCurrentStepIndex = useCallback(
    (nextStepIndex: number) => {
      const clampedStepIndex = clampStepIndex(nextStepIndex);
      setCurrentStepIndexState(clampedStepIndex);
      persistCurrentSnapshot(clampedStepIndex);
    },
    [persistCurrentSnapshot],
  );

  const requestClose = useCallback(() => {
    persistCurrentSnapshot();
    setIsCloseConfirmationOpen(true);
  }, [persistCurrentSnapshot]);

  const cancelClose = useCallback(() => {
    setIsCloseConfirmationOpen(false);
  }, []);

  const confirmClose = useCallback(() => {
    setIsCloseConfirmationOpen(false);
    clearPersistedFiles();
    clearPersistedDraft();
    clearDraft();
    resetFormFlow();
    onClose();
  }, [
    clearDraft,
    clearPersistedDraft,
    clearPersistedFiles,
    onClose,
    resetFormFlow,
  ]);

  const closeAfterSuccess = useCallback(() => {
    clearPersistedFiles();
    clearPersistedDraft();
    clearDraft();
    resetFormFlow();
    onClose();
  }, [
    clearDraft,
    clearPersistedDraft,
    clearPersistedFiles,
    onClose,
    resetFormFlow,
  ]);

  useEffect(() => {
    if (!isHydrated) {
      return;
    }

    if (open && !wasOpenRef.current) {
      setIsCloseConfirmationOpen(false);

      if (draftState.isActive) {
        expandDraft();
      } else {
        const startStep = clampDraftStep(initialStep);
        resetFormFlow();
        startDraft(createInitialDraft(startStep));
        setCurrentStepIndexState(startStep - 1);
      }
    }

    wasOpenRef.current = open;
  }, [
    draftState.isActive,
    expandDraft,
    initialStep,
    isHydrated,
    open,
    resetFormFlow,
    startDraft,
  ]);

  useEffect(() => {
    if (!isHydrated || !draftState.isActive) {
      hydratedDraftRef.current = false;
      isRestoringDraftRef.current = false;
      setRestoredAvisImpositionFiles(undefined);
      return;
    }

    if (hydratedDraftRef.current) {
      return;
    }

    hydratedDraftRef.current = true;
    isRestoringDraftRef.current = true;

    resetFormFlow();

    const restoredValues = deserializeDraftValues(draftState.draft.values);
    form.setFieldsValue(restoredValues);
    setCurrentStepIndexState(clampDraftStep(draftState.draft.currentStep) - 1);

    let isCancelled = false;
    const restoreFiles = async () => {
      try {
        const [devisFiles, otherFiles, avisImpositionFiles] = await Promise.all(
          [
            restoreDevisFiles(),
            restoreOtherFiles(),
            restoreAvisImpositionFiles(),
          ],
        );

        if (isCancelled) {
          return;
        }

        const currentDevis = form.getFieldValue('devis') as
          | UploadFile[]
          | undefined;
        const currentOtherFiles = form.getFieldValue('otherFiles') as
          | UploadFile[]
          | undefined;
        const currentAvisImposition = form.getFieldValue('avisImposition') as
          | UploadFile[]
          | undefined;

        form.setFieldsValue({
          devis: currentDevis?.length ? currentDevis : devisFiles,
          otherFiles: currentOtherFiles?.length
            ? currentOtherFiles
            : otherFiles,
          avisImposition: currentAvisImposition?.length
            ? currentAvisImposition
            : avisImpositionFiles,
        });

        setRestoredAvisImpositionFiles(
          avisImpositionFiles.length ? avisImpositionFiles : undefined,
        );
      } finally {
        if (!isCancelled) {
          isRestoringDraftRef.current = false;
        }
      }
    };

    restoreFiles().catch(() => {
      if (!isCancelled) {
        isRestoringDraftRef.current = false;
      }
    });

    return () => {
      isCancelled = true;
      isRestoringDraftRef.current = false;
    };
  }, [
    draftState.draft.currentStep,
    draftState.draft.values,
    draftState.isActive,
    form,
    isHydrated,
    resetFormFlow,
    restoreAvisImpositionFiles,
    restoreDevisFiles,
    restoreOtherFiles,
  ]);

  const isWizardOpen =
    draftState.isActive && !draftState.isMinimized && !isSubmitSuccess;
  const isSuccessOpen =
    draftState.isActive && !draftState.isMinimized && isSubmitSuccess;

  return {
    isHydrated,
    currentStepIndex,
    setCurrentStepIndex,
    isWizardOpen,
    isSuccessOpen,
    isCloseConfirmationOpen,
    isDraftActive: draftState.isActive,
    isDraftMinimized: draftState.isMinimized,
    restoredAvisImpositionFiles,
    onDevisChange,
    onOtherFilesChange,
    onAvisImpositionChange,
    requestMinimize,
    expandFromMinimized: expandDraft,
    isMinimizing,
    minimizeTargetRef,
    requestClose,
    cancelClose,
    confirmClose,
    closeAfterSuccess,
  };
}
